// src/components/common/FlagBadge.js
//
// Sticker epoxy de bandera de país.
//
// Visualmente busca el efecto de un sticker pequeño con relieve:
//   - Bandera recortada a círculo con background-size: cover. Las banderas
//     de América son todas landscape (W>H), así que `cover` deja el alto
//     completo (no se recortan franjas superior/inferior tipo Brasil, EEUU,
//     Canadá) y solo se cropean los costados, que es el comportamiento
//     deseado para un sticker redondo.
//   - Brillo especular radial en la esquina superior izquierda (highlight).
//   - Gradiente vertical leve (claro arriba, oscuro abajo) que da curvatura.
//   - Inset shadows blanco-arriba / negro-abajo simulando el rim del epoxy.
//   - Drop shadow externa suave para que el sticker "se despegue" del fondo.
//
// Uso:
//   <FlagBadge country={profile.country} size="sm" />
//   <FlagBadge country="AR" size="md" title="Argentina" />
//
// Si no se puede resolver el país, no renderiza nada (graceful fallback).

import React from 'react';
import { resolveCountryCode, getFlagSvgUrl } from '../../utils/countryFlag';

const SIZE_PX = {
  xs: 14,   // inline con texto chico, posts del feed
  sm: 18,   // cards compactas
  md: 22,   // cards detailed (ProfileCard estándar)
  lg: 28    // hero / cabeceras de perfil
};

const FlagBadge = React.memo(function FlagBadge({
  country,
  size = 'sm',
  title,
  className = '',
  ariaLabel
}) {
  const code = resolveCountryCode(country);
  if (!code) return null;

  const px = SIZE_PX[size] || SIZE_PX.sm;
  const flagUrl = getFlagSvgUrl(code);
  const label = title || ariaLabel || `Bandera de ${country}`;

  return (
    <span
      role="img"
      aria-label={label}
      title={title || country}
      className={`relative inline-block rounded-full align-middle ${className}`}
      style={{
        width: `${px}px`,
        height: `${px}px`,
        // Sombra externa: profundidad + rim oscuro de contención
        boxShadow:
          '0 1px 2px rgba(0,0,0,0.45), 0 0 0 0.5px rgba(0,0,0,0.55)',
        // La bandera va de background así nos queda recortada al círculo.
        // `cover` mantiene el aspect ratio del SVG — para banderas landscape
        // el alto llena el círculo y se recortan los lados (no el top/bottom).
        backgroundImage: `url(${flagUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Capa de curvatura + rim interno (luz arriba, sombra abajo) */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 38%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.22) 100%)',
          boxShadow:
            'inset 0 1px 1px rgba(255,255,255,0.55), inset 0 -1px 1.5px rgba(0,0,0,0.45)'
        }}
      />

      {/* Brillo especular (highlight de epoxy) */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 55% 40% at 30% 22%, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.25) 35%, rgba(255,255,255,0) 65%)'
        }}
      />
    </span>
  );
});

export default FlagBadge;
